import React from 'react';
import styled from 'styled-components';
import { WaiverSection } from '../../types/waiver';

const PreviewWrapper = styled.div<{ $scale?: number }>`
  transform: scale(${({ $scale }) => $scale || 1});
  transform-origin: top left;
  width: ${({ $scale }) => $scale ? `${100 / $scale}%` : '100%'};
`;

const PreviewContainer = styled.div<{ $compact?: boolean }>`
  background: white;
  color: #333;
  padding: ${({ $compact }) => $compact ? '12px' : '40px'};
  border-radius: 8px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PreviewHeader = styled.div<{ $compact?: boolean }>`
  text-align: center;
  margin-bottom: ${({ $compact }) => $compact ? '12px' : '32px'};
  padding-bottom: ${({ $compact }) => $compact ? '10px' : '24px'};
  border-bottom: 2px solid #eee;
`;

const PreviewTitle = styled.h1<{ $compact?: boolean }>`
  margin: 0;
  color: #222;
  font-size: ${({ $compact }) => $compact ? '18px' : '24px'};
`;

const Heading = styled.div<{ $level: number; $align?: string }>`
  font-size: ${({ $level }) => {
    switch ($level) {
      case 1: return '28px';
      case 2: return '24px';
      case 3: return '20px';
      case 4: return '18px';
      default: return '16px';
    }
  }};
  font-weight: ${({ $level }) => ($level <= 2 ? 700 : 600)};
  color: #222;
  margin: 24px 0 16px 0;
  text-align: ${({ $align }) => $align || 'left'};

  &:first-child {
    margin-top: 0;
  }
`;

const RichText = styled.div<{ $align?: string }>`
  font-size: 15px;
  line-height: 1.7;
  color: #444;
  margin: 16px 0;
  text-align: ${({ $align }) => $align || 'left'};

  p {
    margin: 12px 0;
  }

  strong {
    font-weight: 600;
    color: #222;
  }
`;

const ImageContainer = styled.div`
  margin: 24px 0;
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ddd;
  margin: 32px 0;
`;

const BulletList = styled.ul`
  margin: 16px 0;
  padding-left: 24px;

  li {
    font-size: 15px;
    line-height: 1.6;
    color: #444;
    margin: 8px 0;
  }
`;

const NumberedList = styled.ol`
  margin: 16px 0;
  padding-left: 24px;

  li {
    font-size: 15px;
    line-height: 1.6;
    color: #444;
    margin: 8px 0;
  }
`;

const EmptySection = styled.div`
  padding: 24px;
  text-align: center;
  color: #999;
  font-style: italic;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 16px 0;
`;

interface WaiverPreviewProps {
  title: string;
  sections: WaiverSection[];
  scale?: number;
  compact?: boolean;
}

const WaiverPreview: React.FC<WaiverPreviewProps> = ({ title, sections, scale, compact }) => {
  const renderSection = (section: WaiverSection) => {
    switch (section.type) {
      case 'heading':
        // Alignment stored in items[0]
        const headingAlign = section.items?.[0] || 'left';
        return (
          <Heading key={section.id} $level={section.headingLevel} $align={headingAlign}>
            {section.content || 'Heading text...'}
          </Heading>
        );

      case 'rich_text':
        if (!section.content) {
          return <EmptySection key={section.id}>Text content...</EmptySection>;
        }
        // Convert newlines to <br> tags for proper display
        const htmlContent = section.content.replace(/\n/g, '<br>');
        // Alignment: 1=left, 2=center, 3=right
        const alignMap: Record<number, string> = { 1: 'left', 2: 'center', 3: 'right' };
        const textAlign = alignMap[section.headingLevel] || 'left';
        return (
          <RichText
            key={section.id}
            $align={textAlign}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );

      case 'image':
        if (!section.content) {
          return <EmptySection key={section.id}>Image placeholder</EmptySection>;
        }
        // Size from headingLevel: 1=small, 2=medium, 3=large, 4=full
        const sizeMap: Record<number, string> = { 1: '150px', 2: '250px', 3: '400px', 4: '100%' };
        const imageSize = sizeMap[section.headingLevel] || '250px';
        return (
          <ImageContainer key={section.id}>
            <img src={section.content} alt="Waiver content" style={{ maxWidth: imageSize }} />
          </ImageContainer>
        );

      case 'divider':
        return <Divider key={section.id} />;

      case 'bullet_list':
        if (!section.items || section.items.length === 0) {
          return <EmptySection key={section.id}>Bullet list items...</EmptySection>;
        }
        return (
          <BulletList key={section.id}>
            {section.items.map((item, index) => (
              <li key={index}>{item || 'List item...'}</li>
            ))}
          </BulletList>
        );

      case 'numbered_list':
        if (!section.items || section.items.length === 0) {
          return <EmptySection key={section.id}>Numbered list items...</EmptySection>;
        }
        return (
          <NumberedList key={section.id}>
            {section.items.map((item, index) => (
              <li key={index}>{item || 'List item...'}</li>
            ))}
          </NumberedList>
        );

      default:
        return null;
    }
  };

  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  return (
    <PreviewWrapper $scale={scale}>
      <PreviewContainer $compact={compact}>
        <PreviewHeader $compact={compact}>
          <PreviewTitle $compact={compact}>{title || 'Waiver Agreement'}</PreviewTitle>
        </PreviewHeader>

        {sortedSections.length === 0 ? (
          <EmptySection>
            No content sections yet. Add sections using the builder on the left.
          </EmptySection>
        ) : (
          sortedSections.map(renderSection)
        )}
      </PreviewContainer>
    </PreviewWrapper>
  );
};

export default WaiverPreview;
